import { useCallback, useMemo } from 'react';
import { useWeb3React } from '@web3-react/core';
import { config } from '../../../config/config';
import { EnabledChains } from '../../../config/constants';
import useLiquidityPools from './useLiquidityPools';
// import useTokenDetails from '../useTokenDetails';
import { LockPoolDetails, UserLockUps } from './useUserLockUps';

const mockLockUpData: Record<string, LockPoolDetails[]> = {
    blueChip: [
        {
            lockUp: undefined,
            totalLp: '1250.50',
            unlockedLp: '980.20',
            lockedLp: '270.30',
            unlockedLpRatio: '78',
            lockedLpRatio: '22',
            totalAssets: '5200.75',
            unlockedAssets: '4100.50',
            lockedAssets: '1100.25',
            unlockedAssetsRatio: '79',
            lockedAssetsRatio: '21',
            totalPoolShares: '1250500000000000000000',
            unlockedPoolShares: '980200000000000000000',
        },
        {
            lockUp: { lockUpId: 0, duration: 30, feeMultiplier: 1.25 },
            totalLp: '800.00',
            unlockedLp: '400.00',
            lockedLp: '400.00',
            unlockedLpRatio: '50',
            lockedLpRatio: '50',
            totalAssets: '3400.00',
            unlockedAssets: '1700.00',
            lockedAssets: '1700.00',
            unlockedAssetsRatio: '50',
            lockedAssetsRatio: '50',
            totalPoolShares: '800000000000000000000',
            unlockedPoolShares: '400000000000000000000',
        },
        {
            lockUp: { lockUpId: 1, duration: 60, feeMultiplier: 1.5 },
            totalLp: '500.00',
            unlockedLp: '0',
            lockedLp: '500.00',
            unlockedLpRatio: '0',
            lockedLpRatio: '100',
            totalAssets: '2150.00',
            unlockedAssets: '0',
            lockedAssets: '2150.00',
            unlockedAssetsRatio: '0',
            lockedAssetsRatio: '100',
            totalPoolShares: '500000000000000000000',
            unlockedPoolShares: '0',
        },
    ],
    altCoin: [
        {
            lockUp: undefined,
            totalLp: '600.25',
            unlockedLp: '500.00',
            lockedLp: '100.25',
            unlockedLpRatio: '83',
            lockedLpRatio: '17',
            totalAssets: '1800.50',
            unlockedAssets: '1500.00',
            lockedAssets: '300.50',
            unlockedAssetsRatio: '83',
            lockedAssetsRatio: '17',
            totalPoolShares: '600250000000000000000',
            unlockedPoolShares: '500000000000000000000',
        },
        {
            lockUp: { lockUpId: 0, duration: 30, feeMultiplier: 1.25 },
            totalLp: '350.00',
            unlockedLp: '175.00',
            lockedLp: '175.00',
            unlockedLpRatio: '50',
            lockedLpRatio: '50',
            totalAssets: '1050.00',
            unlockedAssets: '525.00',
            lockedAssets: '525.00',
            unlockedAssetsRatio: '50',
            lockedAssetsRatio: '50',
            totalPoolShares: '350000000000000000000',
            unlockedPoolShares: '175000000000000000000',
        },
    ],
    degen: [
        {
            lockUp: undefined,
            totalLp: '200.00',
            unlockedLp: '200.00',
            lockedLp: '0',
            unlockedLpRatio: '100',
            lockedLpRatio: '0',
            totalAssets: '600.00',
            unlockedAssets: '600.00',
            lockedAssets: '0',
            unlockedAssetsRatio: '100',
            lockedAssetsRatio: '0',
            totalPoolShares: '200000000000000000000',
            unlockedPoolShares: '200000000000000000000',
        },
        {
            lockUp: { lockUpId: 1, duration: 60, feeMultiplier: 1.5 },
            totalLp: '150.00',
            unlockedLp: '0',
            lockedLp: '150.00',
            unlockedLpRatio: '0',
            lockedLpRatio: '100',
            totalAssets: '450.00',
            unlockedAssets: '0',
            lockedAssets: '450.00',
            unlockedAssetsRatio: '0',
            lockedAssetsRatio: '100',
            totalPoolShares: '150000000000000000000',
            unlockedPoolShares: '0',
        },
        {
            lockUp: { lockUpId: 2, duration: 90, feeMultiplier: 2.0 },
            totalLp: '100.00',
            unlockedLp: '50.00',
            lockedLp: '50.00',
            unlockedLpRatio: '50',
            lockedLpRatio: '50',
            totalAssets: '300.00',
            unlockedAssets: '150.00',
            lockedAssets: '150.00',
            unlockedAssetsRatio: '50',
            lockedAssetsRatio: '50',
            totalPoolShares: '100000000000000000000',
            unlockedPoolShares: '50000000000000000000',
        },
    ],
};

const useMockUserLockUps = () => {
    const { chainId } = useWeb3React();
    const { liquidityPools } = useLiquidityPools();
    // const { decimals } = useTokenDetails(
    //     config.addresses[chainId as EnabledChains[number]]?.collateral
    // );

    const userLockUps = useMemo<UserLockUps>(() => {
        if (!liquidityPools) return {};

        const result: UserLockUps = {};

        liquidityPools.forEach((pool) => {
            let poolKey: string | undefined;
            if (
                pool.poolId.toLowerCase() ===
                config.addresses[
                    chainId as EnabledChains[number]
                ]?.liquidityPools.blueChip?.toLowerCase()
            ) {
                poolKey = 'blueChip';
            } else if (
                pool.poolId.toLowerCase() ===
                config.addresses[
                    chainId as EnabledChains[number]
                ]?.liquidityPools.altCoin?.toLowerCase()
            ) {
                poolKey = 'altCoin';
            } else if (
                pool.poolId.toLowerCase() ===
                config.addresses[
                    chainId as EnabledChains[number]
                ]?.liquidityPools.degen?.toLowerCase()
            ) {
                poolKey = 'degen';
            }

            const details = poolKey ? mockLockUpData[poolKey] ?? [] : [];

            result[pool.poolId] = {
                liquidityPool: pool,
                details,
                lastDepositTimestamp: Math.floor(Date.now() / 1000) - 86400 * 7,
            };
        });

        return result;
    }, [liquidityPools, chainId]);

    const updateUserLockUps = useCallback(() => {}, []);

    return {
        userLockUps,
        isLoading: false,
        error: undefined,
        updateUserLockUps,
    };
};

export default useMockUserLockUps;
